import React, { CSSProperties, useState } from 'react';
import { Link, withRouter, RouteComponentProps } from 'react-router-dom';
import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import * as MuiIcons from '@mui/icons-material';

import Img from 'retired/Img';
import User from 'entities/user/model';
import { purple, gray4 } from 'utils/color-styles';
import BetaIcon from 'assets/icons/beta.svg';
import { Features } from 'utils/features';
import { Privileges } from 'utils/privileges';
import Organization from 'entities/organization/model';

import { AppMenuLinkInterface, getAppMenuLinkCollection } from './appMenuLinkDef';

const linkStyles: CSSProperties = {
  position: 'relative',
  width: 'calc(100% - 20px)',
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'row',
  textAlign: 'center',
  padding: '5px 0 5px 25px',
};

const activeLinkStyles: CSSProperties = {
  margin: '20px 10px',
  backgroundColor: '#DED7FF',
  borderRadius: 20,
  position: 'relative',
};

const iconStyles: CSSProperties = {
  width: 32,
  height: 32,
  marginRight: 35,
};

const textStyle: CSSProperties = {
  color: '#000000',
  fontWeight: 400,
  fontSize: 13,
  marginBottom: 0,
};

const betaIconStyle: CSSProperties = {
  position: 'absolute',
  top: 3,
  left: 9,
};

interface SubInterface {
  to: string;
  icon: string;
  activeIcon: string;
  title: string;
  privilegeNeeded?: Privileges[];
  featureNeeded?: Features[];
  beta?: boolean;
  iconType?: 'svg' | 'mui';
}

interface AppMenuLinkProps extends RouteComponentProps<any> {
  to: string;
  icon: string;
  activeIcon: string;
  title: string;
  user: User;
  imageStyle?: CSSProperties;
  sub?: SubInterface[];
  beta?: boolean;
  iconType?: 'svg' | 'mui';
}

const renderIcon = (
  iconName: string,
  iconType: 'svg' | 'mui' = 'svg',
  isActive: boolean,
  style: CSSProperties,
  alt: string
) => {
  if (iconType === 'mui') {
    // MUI icon
    const IconComponent = (MuiIcons as any)[iconName];
    if (IconComponent) {
      return <IconComponent style={{ ...style, color: isActive ? purple : '#000000' }} />;
    }
    // Fallback if icon not found
    console.warn(`MUI icon "${iconName}" not found`);
    return <div style={style}>?</div>;
  }
  // SVG icon (string path)
  return <Img src={iconName} alt={alt} style={style} />;
};

const AppMenuLink = withRouter((props: AppMenuLinkProps) => {
  const isActive =
    (props.to.includes('/deeds/') && props.location.pathname.includes('/deeds/')) ||
    (props.to === '/volunteers' && props.location.pathname.startsWith('/volunteers/')) ||
    (props.to === '/donations' &&
      (props.location.pathname.startsWith('/donations/') ||
        props.location.pathname.startsWith('/invoices'))) ||
    (props.to === '/organization' &&
      !props.location.pathname.includes('/organizations') &&
      (props.location.pathname.startsWith('/organiz') ||
        props.location.pathname === '/user' ||
        props.location.pathname === '/app-settings' ||
        props.location.pathname === '/deed-sourcing' ||
        props.location.pathname.startsWith('/locations'))) ||
    (props.to === '/forms' &&
      (props.location.pathname.startsWith('/forms') ||
        props.location.pathname.startsWith('/surveys'))) ||
    (props.to === '/organizations' && props.location.pathname.startsWith('/organizations/')) ||
    (props.to === '/back-office' && props.location.pathname.startsWith('/back-office')) ||
    (props.to.length > 1 && !props.location.pathname.includes('/organizations')
      ? props.location.pathname.startsWith(props.to)
      : props.location.pathname === props.to);

  const [subMenuOpen, setSubMenuOpen] = useState(false);
  const Icon = subMenuOpen ? ArrowDropUpIcon : ArrowDropDown;

  const showSubs = props.sub
    ?.map(
      (s) =>
        (s.featureNeeded?.length
          ? s.featureNeeded.some((item: Features) => props.user.hasFeature(item))
          : true) &&
        (s.privilegeNeeded?.length
          ? s.privilegeNeeded.some((item: Privileges) => props.user.hasPrivilege(item))
          : true)
    )
    .some(Boolean);

  return (
    <div style={isActive ? activeLinkStyles : { margin: '20px 10px', position: 'relative' }}>
      <Link
        to={
          props.to === '/deeds/events' &&
          /\/deeds\/(events|campaigns|projects)/.test(props.location.pathname)
            ? `/deeds/${props.location.pathname.split('/')[2]}`
            : props.to
        }
        style={linkStyles}
        title={props.title}
      >
        {renderIcon(
          isActive ? props.activeIcon : props.icon,
          props.iconType || 'svg',
          isActive,
          iconStyles,
          props.title || ''
        )}

        {props.beta ? <Img src={BetaIcon} style={betaIconStyle} alt="Beta icon" /> : null}

        <div style={textStyle}>{props.title}</div>

        {props.sub && showSubs && (
          <span
            style={{
              position: 'absolute',
              right: -16,
              color: isActive ? purple : gray4,
            }}
          >
            <Icon
              onClick={(e) => {
                e.preventDefault();
                setSubMenuOpen(!subMenuOpen);
              }}
            />
          </span>
        )}
      </Link>
      <div
        style={{
          display: subMenuOpen ? 'block' : 'none',
          padding: '15px 4px 4px',
        }}
      >
        {props.sub?.map((s) => {
          const hasPrivileges = s.privilegeNeeded?.length
            ? s.privilegeNeeded
                .map((item: Privileges) => props.user.hasPrivilege(item))
                .some(Boolean)
            : true;

          const hasFeatures = s.featureNeeded?.length
            ? s.featureNeeded.map((item: string) => props.user.hasFeature(item)).some(Boolean)
            : true;

          return hasPrivileges && hasFeatures ? (
            <Link
              key={s.title}
              to={
                s.to === '/deeds/events' &&
                /\/deeds\/(events|campaigns|projects)/.test(s.location.pathname)
                  ? `/deeds/${s.location.pathname.split('/')[2]}`
                  : s.to
              }
              style={{
                ...linkStyles,
                width: 'auto',
                padding: '10px 0 10px 8px',
                backgroundColor:
                  props.location.pathname.startsWith(s.to) && subMenuOpen
                    ? '#c0b3ff'
                    : 'transparent',
                borderRadius: 20,
              }}
              title={s.title}
            >
              {renderIcon(
                isActive ? s.activeIcon : s.icon,
                s.iconType || 'svg',
                props.location.pathname.startsWith(s.to) && subMenuOpen,
                {
                  ...iconStyles,
                  width: 24,
                  height: 24,
                  marginLeft: 18,
                  marginRight: 38,
                },
                s.title || ''
              )}

              {s.beta ? <Img src={BetaIcon} style={betaIconStyle} alt="Beta icon" /> : null}

              <div style={textStyle}>{s.title}</div>
            </Link>
          ) : null;
        })}
      </div>
    </div>
  );
});

interface AppMenuProps {
  organization: Organization;
  user: User;
}

const AppMenu = (props: AppMenuProps): JSX.Element => {
  const { organization, user } = props;
  return (
    <>
      <div style={{ overflowY: 'auto', overflowX: 'hidden', flexGrow: 1 }}>
        {getAppMenuLinkCollection(organization, user).map(
          (appLink: AppMenuLinkInterface): JSX.Element | null => {
            const hasPrivileges = appLink.privilegeNeeded.length
              ? appLink.privilegeNeeded.some((item: Privileges) => user.hasPrivilege(item))
              : true;

            const hasFeatures = appLink.featureNeeded.length
              ? appLink.featureNeeded.some((item: Features) => user.hasFeature(item))
              : true;

            return hasPrivileges && hasFeatures ? (
              <AppMenuLink
                key={appLink.to}
                to={appLink.to}
                icon={appLink.icon}
                activeIcon={appLink.activeIcon}
                title={appLink.title}
                imageStyle={appLink.imageStyle}
                sub={appLink.sub}
                user={user}
                beta={appLink.beta}
                iconType={appLink.iconType}
              />
            ) : null;
          }
        )}
      </div>
      <div
        style={{
          width: 50,
          height: 50,
          right: -50,
          top: 0,
          position: 'absolute',
          backgroundColor: '#fff',
        }}
      >
        <div
          style={{
            width: 50,
            height: 50,
            backgroundColor: '#F7F7F7',
            borderTopLeftRadius: 25,
          }}
        />
      </div>
    </>
  );
};

export default AppMenu;
